import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Loader2, Clock, CalendarX } from "lucide-react";

/**
 * AvailabilityEditor — Acuity-style weekly recurring availability.
 *
 * Practitioners define which weekday windows (e.g., "Mon 9:00–17:00")
 * they accept bookings in, plus optional per-date blackout overrides.
 * The public /providers/{uid}/availability endpoint expands these rules
 * into concrete bookable slot instances that clients pick before paying.
 *
 * Rendered inside Profile.jsx under a new "Availability" tab, next to
 * Services. Owner-only surface.
 */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatMin(m) {
  const h = Math.floor(m / 60).toString().padStart(2, "0");
  const mm = (m % 60).toString().padStart(2, "0");
  return `${h}:${mm}`;
}
function parseTime(s) {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + (m || 0);
}

export default function AvailabilityEditor() {
  const [rules, setRules] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [adding, setAdding] = useState({ dayOfWeek: 0, startTime: "09:00", endTime: "17:00", slotMinutes: 60 });
  const [saving, setSaving] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [prefsSaving, setPrefsSaving] = useState(false);

  const load = async () => {
    try {
      const [r, o, p] = await Promise.all([
        api.get("/providers/me/availability-rules"),
        api.get("/providers/me/availability-overrides"),
        api.get("/providers/me/booking-preferences"),
      ]);
      setRules(r.data);
      setOverrides(o.data);
      setPrefs(p.data);
    } catch {
      toast.error("Failed to load availability");
    }
  };
  useEffect(() => { load(); }, []);

  const addRule = async () => {
    const startMin = parseTime(adding.startTime);
    const endMin = parseTime(adding.endTime);
    if (endMin <= startMin) return toast.error("End time must be after start");
    setSaving(true);
    try {
      await api.post("/providers/me/availability-rules", {
        dayOfWeek: Number(adding.dayOfWeek),
        startMin, endMin,
        slotMinutes: Number(adding.slotMinutes),
        timezone: "UTC",
      });
      toast.success("Availability added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rid) => {
    if (!window.confirm("Remove this availability window?")) return;
    try {
      await api.delete(`/providers/me/availability-rules/${rid}`);
      toast.success("Removed");
      await load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const blockDay = async () => {
    if (!blockDate) return;
    try {
      await api.post("/providers/me/availability-overrides", {
        date: blockDate, blocked: true, reason: "Blocked by practitioner",
      });
      toast.success("Day blocked");
      setBlockDate("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const unblockDay = async (date) => {
    try {
      await api.delete(`/providers/me/availability-overrides/${date}`);
      toast.success("Day unblocked");
      await load();
    } catch {
      toast.error("Failed");
    }
  };

  if (rules === null) return <div className="text-foreground/55 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="availability-editor">
      <div>
        <p className="text-foreground/60 text-sm leading-relaxed">
          Set your recurring weekly hours. Clients booking a service see slots in
          their local timezone; you enter them in <strong>UTC</strong> here. Convert your local time
          to UTC once, then the app handles the rest.
        </p>
      </div>

      {/* Weekly grid */}
      <div className="parchment p-5">
        <div className="label-mystic text-secondary mb-3">Weekly hours</div>
        {rules.length === 0 ? (
          <div className="text-sm text-foreground/55 italic">No availability set — clients cannot book yet. Add a window below.</div>
        ) : (
          <ul className="divide-y divide-foreground/5">
            {rules.map((r) => (
              <li key={r.id} className="py-2.5 flex items-center gap-4" data-testid={`avail-rule-${r.id}`}>
                <span className="w-12 text-xs uppercase tracking-wider text-secondary shrink-0">{DAYS[r.dayOfWeek]}</span>
                <span className="flex-1 text-sm">
                  <span className="font-mono">{formatMin(r.startMin)}–{formatMin(r.endMin)}</span>
                  <span className="text-foreground/50 ml-3">{r.slotMinutes}-min slots</span>
                </span>
                <button
                  onClick={() => deleteRule(r.id)}
                  className="text-foreground/40 hover:text-destructive p-1.5"
                  data-testid={`avail-delete-${r.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add rule */}
      <div className="parchment p-5">
        <div className="label-mystic text-secondary mb-3">Add availability window</div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-[11px] text-foreground/60 uppercase tracking-wider">Day</label>
            <select
              value={adding.dayOfWeek}
              onChange={(e) => setAdding({ ...adding, dayOfWeek: e.target.value })}
              className="w-full bg-input border border-secondary/25 rounded-sm p-2.5 mt-1 text-sm"
              data-testid="avail-add-day"
            >
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-foreground/60 uppercase tracking-wider">Start</label>
            <input
              type="time" value={adding.startTime}
              onChange={(e) => setAdding({ ...adding, startTime: e.target.value })}
              className="w-full bg-input border border-secondary/25 rounded-sm p-2.5 mt-1 text-sm font-mono"
              data-testid="avail-add-start"
            />
          </div>
          <div>
            <label className="text-[11px] text-foreground/60 uppercase tracking-wider">End</label>
            <input
              type="time" value={adding.endTime}
              onChange={(e) => setAdding({ ...adding, endTime: e.target.value })}
              className="w-full bg-input border border-secondary/25 rounded-sm p-2.5 mt-1 text-sm font-mono"
              data-testid="avail-add-end"
            />
          </div>
          <div>
            <label className="text-[11px] text-foreground/60 uppercase tracking-wider">Slot length</label>
            <select
              value={adding.slotMinutes}
              onChange={(e) => setAdding({ ...adding, slotMinutes: e.target.value })}
              className="w-full bg-input border border-secondary/25 rounded-sm p-2.5 mt-1 text-sm"
              data-testid="avail-add-slot"
            >
              {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </div>
          <button
            onClick={addRule}
            disabled={saving}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-60 py-2.5 rounded-sm uppercase tracking-[0.16em] text-xs font-semibold inline-flex items-center justify-center gap-2"
            data-testid="avail-add-btn"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {/* Booking policies (Acuity-style) */}
      <div className="parchment p-5" data-testid="booking-policies">
        <div className="label-mystic text-secondary mb-3">Booking policies</div>
        {!prefs ? (
          <div className="text-sm text-foreground/55">Loading…</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 items-end">
            <NumField label="Min notice (hours)" hint="Earliest a client can book"
              value={prefs.minNoticeHours} onChange={(v) => setPrefs({ ...prefs, minNoticeHours: v })} min={0} max={168} testid="prefs-min-notice" />
            <NumField label="Max advance (days)" hint="How far ahead clients can book"
              value={prefs.maxAdvanceDays} onChange={(v) => setPrefs({ ...prefs, maxAdvanceDays: v })} min={1} max={365} testid="prefs-max-advance" />
            <NumField label="Buffer between (min)" hint="Break time between sessions"
              value={prefs.bufferMinutes} onChange={(v) => setPrefs({ ...prefs, bufferMinutes: v })} min={0} max={120} testid="prefs-buffer" />
            <NumField label="Cancel window (hours)" hint="Cancellations inside this are flagged 'late'"
              value={prefs.cancellationWindowHours} onChange={(v) => setPrefs({ ...prefs, cancellationWindowHours: v })} min={0} max={168} testid="prefs-cancel-window" />
            <button
              onClick={async () => {
                setPrefsSaving(true);
                try {
                  await api.post("/providers/me/booking-preferences", prefs);
                  toast.success("Policies saved");
                } catch { toast.error("Save failed"); }
                finally { setPrefsSaving(false); }
              }}
              disabled={prefsSaving}
              className="sm:col-span-2 bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-60 py-2.5 rounded-sm uppercase tracking-[0.16em] text-xs font-semibold"
              data-testid="prefs-save-btn"
            >
              {prefsSaving ? "Saving…" : "Save policies"}
            </button>
          </div>
        )}
      </div>

      {/* Blackout dates */}
      <div className="parchment p-5">
        <div className="label-mystic text-secondary mb-3 flex items-center gap-2"><CalendarX className="w-4 h-4" />Blackout dates</div>
        <p className="text-[11px] text-foreground/55 mb-3">Block a specific date (vacation, personal day) — overrides your weekly hours.</p>
        <div className="flex items-end gap-2">
          <input
            type="date" value={blockDate}
            onChange={(e) => setBlockDate(e.target.value)}
            className="bg-input border border-secondary/25 rounded-sm p-2.5 text-sm"
            data-testid="avail-block-date"
          />
          <button
            onClick={blockDay}
            disabled={!blockDate}
            className="border border-destructive/45 text-destructive hover:bg-destructive/10 disabled:opacity-40 px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider"
            data-testid="avail-block-btn"
          >
            Block
          </button>
        </div>
        {overrides.filter((o) => o.blocked).length > 0 && (
          <ul className="mt-4 divide-y divide-foreground/5">
            {overrides.filter((o) => o.blocked).map((o) => (
              <li key={o.date} className="py-2 flex items-center justify-between text-sm">
                <span><Clock className="w-3.5 h-3.5 inline mr-2 text-destructive" />{o.date}</span>
                <button onClick={() => unblockDay(o.date)} className="text-[11px] text-foreground/55 hover:text-secondary uppercase tracking-wider">Unblock</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function NumField({ label, hint, value, onChange, min = 0, max = 999, testid }) {
  return (
    <div>
      <label className="text-[11px] text-foreground/60 uppercase tracking-wider">{label}</label>
      <input
        type="number" value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min} max={max}
        className="w-full bg-input border border-secondary/25 rounded-sm p-2.5 mt-1 text-sm"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}
